import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiMenu, FiX } from 'react-icons/fi';
import Logo from '../../assets/luxistt-logo.png';
import NotificationBell from '../NotificationBell/NotificationBell';
import './navbar.css';

const Navbar = () => {
  const { user, isAdmin, canAccessFrontDesk, canAccessProfilePage, logout } = useAuth();
  /** Owner dashboard (/dashboard) is deprecated in the SPA for now; admins still use it. */
  const showDashboard = isAdmin;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home', end: true },
    { to: '/rooms', label: 'Apartments', end: false },
    { to: '/terms', label: 'Terms', end: false },
    { to: '/contact-us', label: 'Contact', end: false },
  ];

  return (
    <header className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          <img src={Logo} alt="Luxistt" />
        </Link>

        <nav className="navbar-links">
          {navLinks.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `navbar-link${isActive ? ' navbar-link-active' : ''}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="navbar-actions">
          {user ? (
            <>
              <NotificationBell />
              {canAccessFrontDesk && (
                <NavLink
                  to="/front-desk"
                  className={({ isActive }) =>
                    `navbar-btn navbar-btn-ghost${isActive ? ' navbar-btn-active' : ''}`
                  }
                >
                  Front desk
                </NavLink>
              )}
              {showDashboard && (
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `navbar-btn navbar-btn-primary${isActive ? ' navbar-btn-active' : ''}`
                  }
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                to="/my-bookings"
                className={({ isActive }) =>
                  `navbar-btn${showDashboard ? ' navbar-btn-ghost' : ' navbar-btn-primary'}${isActive ? ' navbar-btn-active' : ''}`
                }
              >
                My Bookings
              </NavLink>
              {canAccessProfilePage ? (
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    `navbar-profile${isActive ? ' navbar-profile-active' : ''}`
                  }
                  title="Profile"
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="navbar-avatar" />
                  ) : (
                    <span className="navbar-avatar-fallback" aria-hidden>
                      {user?.name?.charAt(0) || '?'}
                    </span>
                  )}
                  <span className="navbar-profile-label">Profile</span>
                </NavLink>
              ) : null}
              <button
                type="button"
                className="navbar-btn navbar-btn-ghost"
                onClick={() => logout()}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/signup" className="navbar-btn navbar-btn-primary">
                Create account
              </Link>
              <Link to="/login" className="navbar-btn navbar-btn-primary">
                Login
              </Link>
            </>
          )}
        </div>

        <div className="navbar-mobile-quick">
          {user && <NotificationBell />}
          <button
            type="button"
            className="navbar-toggle"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`navbar-mobile ${mobileOpen ? 'navbar-mobile-open' : ''}`}>
        <nav className="navbar-mobile-links">
          {navLinks.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `navbar-mobile-link${isActive ? ' navbar-mobile-link-active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="navbar-mobile-actions">
          {user ? (
            <>
              {canAccessFrontDesk && (
                <NavLink
                  to="/front-desk"
                  className={({ isActive }) =>
                    `navbar-btn navbar-btn-ghost${isActive ? ' navbar-btn-active' : ''}`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  Front desk
                </NavLink>
              )}
              {showDashboard && (
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `navbar-btn navbar-btn-primary${isActive ? ' navbar-btn-active' : ''}`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                to="/my-bookings"
                className={({ isActive }) =>
                  `navbar-btn${showDashboard ? ' navbar-btn-ghost' : ' navbar-btn-primary'}${isActive ? ' navbar-btn-active' : ''}`
                }
                onClick={() => setMobileOpen(false)}
              >
                My Bookings
              </NavLink>
              {canAccessProfilePage ? (
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    `navbar-profile navbar-mobile-profile${isActive ? ' navbar-profile-active' : ''}`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="navbar-avatar" />
                  ) : (
                    <span className="navbar-avatar-fallback" aria-hidden>
                      {user?.name?.charAt(0) || '?'}
                    </span>
                  )}
                  <span className="navbar-profile-label">Profile</span>
                </NavLink>
              ) : null}
              <button
                type="button"
                className="navbar-btn navbar-btn-ghost"
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
              >
                Logout
              </button>
            </>
          ) : null}
        </div>
        {!user && (
          <div className="navbar-mobile-actions-auth">
            <Link
              to="/login"
              className="navbar-btn navbar-btn-outline"
              onClick={() => setMobileOpen(false)}
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="navbar-btn navbar-btn-primary"
              onClick={() => setMobileOpen(false)}
            >
              Create account
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
